import { z } from "zod";
import {
  getTablePermissions,
  getIgnoredColumnsByPermission,
  filterColumnsByPermission,
  hasPermission,
} from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";

export type MarketOption = { value: string; label: string };

const STATUS_OPTIONS = [
  { value: "ONBOARDING", color: "todo" },
  { value: "PENDING", color: "pending" },
  { value: "ACTIVE", color: "complete" },
  { value: "INACTIVE", color: "neutral" },
  { value: "SEPARATE", color: "attention" },
] as const;

// Columns that require read permission on related entities
const RELATIONSHIP_COLUMNS = {
  markets: "markets",
} as const;

export const getUserTableConfig = (
  userRoles: UserRoleType[] | null,
  marketOptions: MarketOption[] = [],
  displayNames?,
) => {
  const permissions = getTablePermissions(userRoles, "users");

  // Get columns to hide based on relationship permissions
  const permissionIgnored = getIgnoredColumnsByPermission(userRoles, RELATIONSHIP_COLUMNS);

  const allFilterChoices = [
    {
      name: "id",
      kind: "text" as const,
      displayName: `${displayNames?.users?.name ?? "User"} Id`,
      menus: ["filter", "visible"] as const,
    },
    { name: "first_name", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "last_name", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "middle_name", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "preferred_name", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "email_address", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "status", kind: "enum" as const, options: [...STATUS_OPTIONS], menus: ["filter", "visible"] as const },
    {
      name: "markets",
      kind: "array" as const,
      displayName: "Markets",
      options: marketOptions,
      menus: ["filter", "visible"] as const,
    },
    { name: "created_at", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
  ];

  return {
    entityName: "User",
    entityNamePlural: "Users",

    tableConfig: {
      readonly: ["id", "created_at"],
      ignore: ["meta_data", "", ...permissionIgnored],
      validators: {
        email_address: (r: { cell: unknown }) => z.string().email().safeParse(r?.cell).success,
      },
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        // if (key === "preferred_name") return value.display_name ?? "";
        if (key === "status") return value ?? "";
        if (key === "meta_data") return "";
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return `${displayNames?.users?.name ?? "User"} Id`;
          case "profiles":
            return "Preferred Name";
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      dataTypes: {
        created_at: "date",
        status: {
          type: "enum",
          options: STATUS_OPTIONS,
        },
        // Only include markets config if user has markets.read permission
        ...(hasPermission(userRoles, "markets", "read") && {
          markets: {
            type: "array",
            picker: "market",
            chipColor: "#4169E1",
            options: marketOptions,
          },
        }),
      },
      writeKeys: {
        // Only include markets writeKey if user has markets.read permission
        ...(hasPermission(userRoles, "markets", "read") && {
          markets: "meta_data.markets",
        }),
      },
    },

    filterChoices: filterColumnsByPermission(userRoles, allFilterChoices, RELATIONSHIP_COLUMNS),
    ...permissions,
    storageKey: "user_flyout",
  };
};
